import { breakLines, calculateTextHeight } from './drawCanvas'
import { type LayoutNode, runsToString } from '@backend/lib/db/typeRoots/layoutNode'

type Point = { x: number; y: number }

export type CanvasView = {
  panX: number
  panY: number
  zoom: number
  virtualWidth: number
  virtualHeight: number
}

export type MouseAction =
  | { type: 'none'; index: number }
  | { type: 'drag'; index: number; offset: { x: number; y: number } }
  | {
      type: 'resize'
      index: number
      corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null
      border: 'top' | 'right' | 'bottom' | 'left' | null
      initialNode: { x: number; y: number; width: number; height: number }
    }
  | { type: 'rotate'; index: number; rotation: number }
  | { type: 'marquee'; start: Point; current: Point }
  | {
      type: 'multi-drag'
      indices: number[]
      offsets: { x: number; y: number }[]
    }
  | {
      type: 'multi-resize'
      indices: number[]
      corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
      initialBounds: { x: number; y: number; width: number; height: number }
      initialNodes: { x: number; y: number; width: number; height: number }[]
    }

export type SnapLine = {
  position: number
  type: 'horizontal' | 'vertical'
  // Index into the nodes array of the node that supplied this snap candidate.
  // Undefined for canvas-border snap lines. Threaded through to drawHandles so
  // the renderer can outline the actual target node, not just the alignment
  // line, which makes it obvious *what* you're aligning to in dense scenes.
  sourceNodeIndex?: number
}

const SNAP_THRESHOLD = 10

export function onMouseDown(
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  nodes: LayoutNode[],
  selectedIndices: number[],
  view: CanvasView,
): MouseAction & { shiftClick?: boolean; clickedIndex?: number } {
  const mouse = getMousePosition(e, canvas, view)
  const isShiftHeld = e.shiftKey

  // For single selection, check controls on the first selected item
  const primarySelectedIndex = selectedIndices.length > 0 ? selectedIndices[0]! : -1

  // Check if clicking on multi-selection bounding box handles. Skip when the selection
  // contains a locked node — a group holding a locked layer can't be group-resized.
  if (selectedIndices.length > 1 && !selectedIndices.some((i) => nodes[i]?.locked)) {
    const bounds = getMultiSelectionBounds(nodes, selectedIndices)
    if (bounds) {
      const cornerClicked = checkMultiSelectCorner(mouse, bounds)
      if (cornerClicked) {
        return {
          type: 'multi-resize',
          indices: selectedIndices,
          corner: cornerClicked,
          initialBounds: bounds,
          initialNodes: selectedIndices.map((i) => ({
            x: nodes[i]!.x,
            y: nodes[i]!.y,
            width: nodes[i]!.width,
            height: nodes[i]!.height,
          })),
        }
      }
    }
  }

  // Check controls on single selected node (locked nodes can't be resized or rotated).
  if (primarySelectedIndex >= 0 && !nodes[primarySelectedIndex]?.locked) {
    const { cornerClicked, borderClicked, isRotationHandle } = selectItem(
      e,
      canvas,
      ctx,
      nodes,
      primarySelectedIndex,
      view,
    )

    if (cornerClicked || borderClicked || isRotationHandle) {
      const selectedNode = nodes[primarySelectedIndex]!
      const initialNode = {
        x: selectedNode.x,
        y: selectedNode.y,
        width: selectedNode.width,
        height: selectedNode.height,
      }

      if (cornerClicked) {
        return {
          type: 'resize',
          index: primarySelectedIndex,
          corner: cornerClicked,
          border: null,
          initialNode,
        }
      }

      if (borderClicked) {
        return {
          type: 'resize',
          index: primarySelectedIndex,
          corner: null,
          border: borderClicked,
          initialNode,
        }
      }

      if (isRotationHandle) {
        return { type: 'rotate', index: primarySelectedIndex, rotation: 0 }
      }
    }
  }

  // Check if clicking on a node
  const topmostNodeIndex = getCollidingNodeIndex(nodes, mouse, ctx)

  if (topmostNodeIndex !== -1) {
    // If shift is held, this is a multi-select toggle
    if (isShiftHeld) {
      return {
        type: 'none',
        index: topmostNodeIndex,
        shiftClick: true,
        clickedIndex: topmostNodeIndex,
      }
    }

    // A locked node stays selectable, but clicking it never starts a drag.
    if (nodes[topmostNodeIndex]?.locked) {
      return { type: 'none', index: topmostNodeIndex, clickedIndex: topmostNodeIndex }
    }

    // If clicking on an already-selected node in a multi-selection, start multi-drag —
    // but never move locked members, so drag only the unlocked nodes.
    if (selectedIndices.includes(topmostNodeIndex) && selectedIndices.length > 1) {
      const dragIndices = selectedIndices.filter((i) => !nodes[i]?.locked)
      const offsets = dragIndices.map((i) => ({
        x: mouse.x - nodes[i]!.x,
        y: mouse.y - nodes[i]!.y,
      }))
      return { type: 'multi-drag', indices: dragIndices, offsets }
    }

    // Single node click - start drag
    const offset = {
      x: mouse.x - nodes[topmostNodeIndex]!.x,
      y: mouse.y - nodes[topmostNodeIndex]!.y,
    }
    return { type: 'drag', index: topmostNodeIndex, offset, clickedIndex: topmostNodeIndex }
  }

  // Clicked on empty canvas - start marquee selection
  return { type: 'marquee', start: mouse, current: mouse }
}

// Calculate bounding box for multiple selected nodes
export function getMultiSelectionBounds(
  nodes: LayoutNode[],
  indices: number[],
): { x: number; y: number; width: number; height: number } | null {
  if (indices.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const i of indices) {
    const node = nodes[i]
    if (!node) continue
    minX = Math.min(minX, node.x)
    minY = Math.min(minY, node.y)
    maxX = Math.max(maxX, node.x + node.width)
    maxY = Math.max(maxY, node.y + node.height)
  }

  if (minX === Infinity) return null

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

function checkMultiSelectCorner(
  mouse: Point,
  bounds: { x: number; y: number; width: number; height: number },
): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null {
  const hitAreaRadius = 12

  const corners: {
    name: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
    point: Point
  }[] = [
    { name: 'top-left', point: { x: bounds.x, y: bounds.y } },
    { name: 'top-right', point: { x: bounds.x + bounds.width, y: bounds.y } },
    { name: 'bottom-left', point: { x: bounds.x, y: bounds.y + bounds.height } },
    { name: 'bottom-right', point: { x: bounds.x + bounds.width, y: bounds.y + bounds.height } },
  ]

  for (const { name, point } of corners) {
    if (
      mouse.x >= point.x - hitAreaRadius &&
      mouse.x <= point.x + hitAreaRadius &&
      mouse.y >= point.y - hitAreaRadius &&
      mouse.y <= point.y + hitAreaRadius
    ) {
      return name
    }
  }

  return null
}

// Get nodes within a marquee selection rectangle
export function getNodesInMarquee(nodes: LayoutNode[], start: Point, end: Point): number[] {
  const left = Math.min(start.x, end.x)
  const right = Math.max(start.x, end.x)
  const top = Math.min(start.y, end.y)
  const bottom = Math.max(start.y, end.y)

  const selected: number[] = []

  nodes.forEach((node, i) => {
    // Locked nodes are excluded from marquee selection (still individually clickable).
    if (node.locked) return
    // Check if node intersects with marquee
    const nodeLeft = node.x
    const nodeRight = node.x + node.width
    const nodeTop = node.y
    const nodeBottom = node.y + node.height

    if (nodeRight >= left && nodeLeft <= right && nodeBottom >= top && nodeTop <= bottom) {
      selected.push(i)
    }
  })

  return selected
}

export function onMouseMove(
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  action: MouseAction,
  nodes: LayoutNode[],
  view: CanvasView,
  _selectedIndices: number[] = [],
) {
  const mouse = getMousePosition(e, canvas, view)
  const hoveredNodeIndex =
    action.type === 'none' || action.type === 'marquee'
      ? getCollidingNodeIndex(nodes, mouse, ctx)
      : -1

  const primaryIndex = 'index' in action ? action.index : -1
  const selectedNodeControls =
    primaryIndex >= 0 && primaryIndex < nodes.length
      ? getSelectedNodeControls(nodes[primaryIndex]!, mouse)
      : undefined

  // Holding Cmd (or Ctrl on Windows/Linux) while dragging suppresses snap —
  // matches Figma's convention. Read live from each move event so the user
  // can press/release mid-drag without restarting the gesture.
  const disableSnap = e.metaKey || e.ctrlKey

  switch (action.type) {
    case 'drag': {
      const x = Math.round(mouse.x - action.offset.x)
      const y = Math.round(mouse.y - action.offset.y)
      const snapped = applySnapping(
        x,
        y,
        nodes[action.index]!,
        nodes,
        view.virtualWidth,
        view.virtualHeight,
        disableSnap,
      )
      return {
        update: {
          node: { x: snapped.x, y: snapped.y },
          index: action.index,
        },
        hoveredNodeIndex,
        snapLines: snapped.snapLines,
      }
    }

    case 'multi-drag': {
      // Calculate new positions without snapping first
      const newPositions = action.indices.map((nodeIndex, i) => {
        const offset = action.offsets[i]!
        return {
          index: nodeIndex,
          x: Math.round(mouse.x - offset.x),
          y: Math.round(mouse.y - offset.y),
        }
      })

      // Calculate bounding box of new positions
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity
      for (const pos of newPositions) {
        const node = nodes[pos.index]!
        minX = Math.min(minX, pos.x)
        minY = Math.min(minY, pos.y)
        maxX = Math.max(maxX, pos.x + node.width)
        maxY = Math.max(maxY, pos.y + node.height)
      }
      const bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY }

      // Apply snapping to bounding box
      const { deltaX, deltaY, snapLines } = applyMultiSnapping(
        bounds,
        action.indices,
        nodes,
        view.virtualWidth,
        view.virtualHeight,
        disableSnap,
      )

      // Apply snap delta to all nodes
      const updates = newPositions.map((pos) => ({
        index: pos.index,
        node: { x: pos.x + deltaX, y: pos.y + deltaY },
      }))

      return {
        update: { multiNodes: updates },
        hoveredNodeIndex,
        snapLines,
      }
    }

    case 'multi-resize': {
      const { initialBounds, initialNodes, indices, corner } = action
      const result = resizeMultiSelection(mouse, initialBounds, initialNodes, indices, corner)
      return {
        update: { multiNodes: result },
        hoveredNodeIndex,
        snapLines: [],
      }
    }

    case 'resize': {
      if (action.corner) {
        const result = resizeCorner(
          e,
          canvas,
          nodes[action.index]!,
          nodes,
          action.corner,
          view,
          action.initialNode,
        )
        if (typeof result === 'string') {
          return {
            update: {
              action: {
                corner: result as Extract<MouseAction, { type: 'resize' }>['corner'],
                border: null,
              },
            },
            hoveredNodeIndex,
          }
        } else {
          return {
            update: {
              node: { x: result.x, y: result.y, width: result.width, height: result.height },
              index: action.index,
            },
            hoveredNodeIndex,
            snapLines: result.snapLines,
          }
        }
      } else if (action.border) {
        const result = resizeBorder(
          e,
          canvas,
          nodes[action.index]!,
          nodes,
          action.border,
          view,
          action.initialNode,
        )
        if (typeof result === 'string') {
          return {
            update: {
              action: {
                corner: null,
                border: result as 'top' | 'right' | 'bottom' | 'left',
              },
            },
            hoveredNodeIndex,
          }
        } else {
          return {
            update: {
              node: { x: result.x, y: result.y, width: result.width, height: result.height },
              index: action.index,
            },
            hoveredNodeIndex,
            snapLines: result.snapLines,
          }
        }
      }
      break
    }

    case 'rotate': {
      const node = nodes[action.index]!
      const nodeCenter = {
        x: node.x + node.width / 2,
        y: node.y + node.height / 2,
      }

      const angle = Math.atan2(mouse.y - nodeCenter.y, mouse.x - nodeCenter.x) * (180 / Math.PI)

      let rotation = angle + 90

      // snap every 15 degrees
      // rotation = Math.round(rotation / 15) * 15

      const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360]
      const snapThreshold = 5

      const closestSnap = snapAngles.find(
        (snapAngle) => Math.abs(rotation - snapAngle) < snapThreshold,
      )

      rotation = closestSnap ?? rotation

      return {
        update: {
          node: { rotation: Math.round(rotation) },
          index: action.index,
        },
        hoveredNodeIndex,
        snapLines: [],
      }
    }

    case 'marquee': {
      return {
        update: {
          marquee: { start: action.start, current: mouse },
        },
        hoveredNodeIndex,
        snapLines: [],
      }
    }
  }

  return { hoveredNodeIndex, selectedNodeControls, snapLines: [] }
}

function resizeMultiSelection(
  mouse: Point,
  initialBounds: { x: number; y: number; width: number; height: number },
  initialNodes: { x: number; y: number; width: number; height: number }[],
  indices: number[],
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
): { index: number; node: { x: number; y: number; width: number; height: number } }[] {
  // Calculate scale factors based on mouse position
  let scaleX = 1
  let scaleY = 1
  let anchorX = initialBounds.x
  let anchorY = initialBounds.y

  switch (corner) {
    case 'bottom-right':
      scaleX = (mouse.x - initialBounds.x) / initialBounds.width
      scaleY = (mouse.y - initialBounds.y) / initialBounds.height
      anchorX = initialBounds.x
      anchorY = initialBounds.y
      break
    case 'bottom-left':
      scaleX = (initialBounds.x + initialBounds.width - mouse.x) / initialBounds.width
      scaleY = (mouse.y - initialBounds.y) / initialBounds.height
      anchorX = initialBounds.x + initialBounds.width
      anchorY = initialBounds.y
      break
    case 'top-right':
      scaleX = (mouse.x - initialBounds.x) / initialBounds.width
      scaleY = (initialBounds.y + initialBounds.height - mouse.y) / initialBounds.height
      anchorX = initialBounds.x
      anchorY = initialBounds.y + initialBounds.height
      break
    case 'top-left':
      scaleX = (initialBounds.x + initialBounds.width - mouse.x) / initialBounds.width
      scaleY = (initialBounds.y + initialBounds.height - mouse.y) / initialBounds.height
      anchorX = initialBounds.x + initialBounds.width
      anchorY = initialBounds.y + initialBounds.height
      break
  }

  // Prevent negative scaling
  scaleX = Math.max(0.1, scaleX)
  scaleY = Math.max(0.1, scaleY)

  // Apply scale to each node
  return indices.map((nodeIndex, i) => {
    const initial = initialNodes[i]!
    const relX = initial.x - anchorX
    const relY = initial.y - anchorY

    return {
      index: nodeIndex,
      node: {
        x: Math.round(anchorX + relX * scaleX),
        y: Math.round(anchorY + relY * scaleY),
        width: Math.round(initial.width * scaleX),
        height: Math.round(initial.height * scaleY),
      },
    }
  })
}

export const selectItem = (
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  nodes: LayoutNode[],
  selectedIndex: number,
  view: CanvasView,
) => {
  const mouse = getMousePosition(e, canvas, view)

  if (selectedIndex >= 0 && selectedIndex < nodes.length) {
    const node = nodes[selectedIndex]!

    const nodeCenter = { x: node.x + node.width / 2, y: node.y + node.height / 2 }
    const rotatedMouse = rotate(mouse, nodeCenter, -node.rotation)

    // Check if the mouse click is within the boundaries of the corners
    const cornerRadius = 8
    const hitAreaRadius = cornerRadius * 1.5
    const borderRadius = 6

    const checkCorner = (corner: Point) => {
      return (
        rotatedMouse.x >= corner.x - hitAreaRadius &&
        rotatedMouse.x <= corner.x + hitAreaRadius &&
        rotatedMouse.y >= corner.y - hitAreaRadius &&
        rotatedMouse.y <= corner.y + hitAreaRadius
      )
    }

    let cornerClicked = null
    let borderClicked = null

    const topLeft = { x: node.x, y: node.y }
    const topRight = { x: node.x + node.width, y: node.y }
    const bottomLeft = { x: node.x, y: node.y + node.height }
    const bottomRight = { x: node.x + node.width, y: node.y + node.height }

    const rotationHandler = {
      x: node.x + node.width / 2,
      y: node.y - 40,
    }

    if (checkCorner(rotationHandler)) {
      return { index: selectedIndex, isRotationHandle: true }
    }

    if (checkCorner(topLeft)) {
      cornerClicked = 'top-left' as const
    } else if (checkCorner(topRight)) {
      cornerClicked = 'top-right' as const
    } else if (checkCorner(bottomLeft)) {
      cornerClicked = 'bottom-left' as const
    } else if (checkCorner(bottomRight)) {
      cornerClicked = 'bottom-right' as const
    }

    if (cornerClicked) {
      return { index: selectedIndex, cornerClicked }
    }

    if (
      rotatedMouse.x >= node.x &&
      rotatedMouse.x <= node.x + node.width &&
      Math.abs(rotatedMouse.y - node.y) <= borderRadius
    ) {
      borderClicked = 'top' as const
    } else if (
      rotatedMouse.x >= node.x &&
      rotatedMouse.x <= node.x + node.width &&
      Math.abs(rotatedMouse.y - (node.y + node.height)) <= borderRadius
    ) {
      borderClicked = 'bottom' as const
    } else if (
      rotatedMouse.y >= node.y &&
      rotatedMouse.y <= node.y + node.height &&
      Math.abs(rotatedMouse.x - node.x) <= borderRadius
    ) {
      borderClicked = 'left' as const
    } else if (
      rotatedMouse.y >= node.y &&
      rotatedMouse.y <= node.y + node.height &&
      Math.abs(rotatedMouse.x - (node.x + node.width)) <= borderRadius
    ) {
      borderClicked = 'right' as const
    }

    if (borderClicked) {
      return { index: selectedIndex, borderClicked }
    }

    if (isWithin(rotatedMouse, node)) {
      return { index: selectedIndex }
    }
  }

  const clickedIndex = getCollidingNodeIndex(nodes, mouse, ctx)
  return { index: clickedIndex }
}

const rotate = (a: Point, b: Point, degree: number): Point => {
  const radians = (degree * Math.PI) / 180
  return {
    x: (a.x - b.x) * Math.cos(radians) - (a.y - b.y) * Math.sin(radians) + b.x,
    y: (a.x - b.x) * Math.sin(radians) + (a.y - b.y) * Math.cos(radians) + b.y,
  }
}

export const getMousePosition = (
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  view: CanvasView,
): Point => {
  const rect = canvas.getBoundingClientRect()
  return {
    x: (e.clientX - rect.left - view.panX) / view.zoom,
    y: (e.clientY - rect.top - view.panY) / view.zoom,
  }
}

const getCollidingNodeIndex = (
  nodes: LayoutNode[],
  mouse: Point,
  ctx: CanvasRenderingContext2D,
) => {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]!

    const nodeCenter = { x: node.x + node.width / 2, y: node.y + node.height / 2 }
    const rotatedMouse = rotate(mouse, nodeCenter, -node.rotation)

    // check if the mouse click is hitting overflowing text
    if (node.data.type === 'text') {
      const { fontFamily, fontWeight, fontStyle, fontSize, lineHeight, verticalAlign } =
        node.data.options

      const padTop = node.data.options.paddingTop ?? 0
      const padRight = node.data.options.paddingRight ?? 0
      const padBottom = node.data.options.paddingBottom ?? 0
      const padLeft = node.data.options.paddingLeft ?? 0
      const innerY = node.y + padTop
      const innerW = Math.max(0, node.width - padLeft - padRight)
      const innerH = Math.max(0, node.height - padTop - padBottom)

      const lines = breakLines(
        runsToString(node.data.content),
        innerW,
        ctx,
        fontSize,
        fontFamily,
        fontWeight,
        fontStyle,
      )
      const textHeight = calculateTextHeight(lines, fontSize, lineHeight)

      const y =
        verticalAlign === 'center'
          ? innerY + innerH / 2 - textHeight / 2
          : verticalAlign === 'bottom'
            ? innerY + innerH - textHeight
            : innerY

      if (isWithin(rotatedMouse, { x: node.x, y: y, width: node.width, height: textHeight })) {
        return i
      }
    }

    if (isWithin(rotatedMouse, node)) return i
  }

  return -1
}

const getSelectedNodeControls = (selectedNode: LayoutNode, mouse: Point) => {
  const nodeCenter = {
    x: selectedNode.x + selectedNode.width / 2,
    y: selectedNode.y + selectedNode.height / 2,
  }
  const rotatedMouse = rotate(mouse, nodeCenter, -selectedNode.rotation)

  const cornerRadius = 8
  const hitAreaRadius = cornerRadius * 1.5
  const borderRadius = 6

  const checkCorner = (corner: Point) => {
    return (
      rotatedMouse.x >= corner.x - hitAreaRadius &&
      rotatedMouse.x <= corner.x + hitAreaRadius &&
      rotatedMouse.y >= corner.y - hitAreaRadius &&
      rotatedMouse.y <= corner.y + hitAreaRadius
    )
  }

  // Check rotation handler
  const rotationHandler = {
    x: selectedNode.x + selectedNode.width / 2,
    y: selectedNode.y - 40,
  }
  if (checkCorner(rotationHandler)) {
    return { isRotationHandle: true }
  }

  // Check corners
  const corners = {
    'top-left': { x: selectedNode.x, y: selectedNode.y },
    'top-right': { x: selectedNode.x + selectedNode.width, y: selectedNode.y },
    'bottom-left': { x: selectedNode.x, y: selectedNode.y + selectedNode.height },
    'bottom-right': {
      x: selectedNode.x + selectedNode.width,
      y: selectedNode.y + selectedNode.height,
    },
  }

  for (const [type, point] of Object.entries(corners)) {
    if (checkCorner(point)) {
      return {
        cornerClicked: type as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
      }
    }
  }

  // Check borders
  if (
    rotatedMouse.x >= selectedNode.x &&
    rotatedMouse.x <= selectedNode.x + selectedNode.width &&
    Math.abs(rotatedMouse.y - selectedNode.y) <= borderRadius
  ) {
    return { borderClicked: 'top' as const }
  } else if (
    rotatedMouse.x >= selectedNode.x &&
    rotatedMouse.x <= selectedNode.x + selectedNode.width &&
    Math.abs(rotatedMouse.y - (selectedNode.y + selectedNode.height)) <= borderRadius
  ) {
    return { borderClicked: 'bottom' as const }
  } else if (
    rotatedMouse.y >= selectedNode.y &&
    rotatedMouse.y <= selectedNode.y + selectedNode.height &&
    Math.abs(rotatedMouse.x - selectedNode.x) <= borderRadius
  ) {
    return { borderClicked: 'left' as const }
  } else if (
    rotatedMouse.y >= selectedNode.y &&
    rotatedMouse.y <= selectedNode.y + selectedNode.height &&
    Math.abs(rotatedMouse.x - (selectedNode.x + selectedNode.width)) <= borderRadius
  ) {
    return { borderClicked: 'right' as const }
  }

  return undefined
}

const isWithin = (
  p: Point,
  box: { x: number; y: number; width: number; height: number },
): boolean => {
  return p.x >= box.x && p.y >= box.y && p.x <= box.x + box.width && p.y <= box.y + box.height
}

const resizeBorder = (
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  node: LayoutNode,
  nodes: LayoutNode[],
  borderClicked: 'top' | 'right' | 'bottom' | 'left',
  view: CanvasView,
  initialNode: { x: number; y: number; width: number; height: number },
) => {
  const mousePosition = getMousePosition(e, canvas, view)
  const altKey = e.altKey

  const { verticalLines, horizontalLines } = getVerticalAndHorizontalLines(
    node,
    nodes,
    view.virtualWidth,
    view.virtualHeight,
  )

  switch (borderClicked) {
    case 'right':
      return resizeRightBorder(node, mousePosition.x, verticalLines, altKey, initialNode)

    case 'left':
      return resizeLeftBorder(node, mousePosition.x, verticalLines, altKey, initialNode)

    case 'top':
      return resizeTopBorder(node, mousePosition.y, horizontalLines, altKey, initialNode)

    case 'bottom':
      return resizeBottomBorder(node, mousePosition.y, horizontalLines, altKey, initialNode)

    default:
      throw new Error('Unknown border clicked')
  }
}

function resizeRightBorder(
  node: LayoutNode,
  mouseX: number,
  verticalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    if (mouseX < centerX) return 'left'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []
    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const halfW = effectiveX - centerX

    return {
      x: Math.round(centerX - halfW),
      y: node.y,
      width: Math.round(halfW * 2),
      height: node.height,
      snapLines: activeSnapLines,
    }
  }

  if (mouseX < node.x) {
    return 'left'
  }

  const snappedRightEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const activeSnapLines: SnapLine[] = []

  if (snappedRightEdge !== mouseX) {
    activeSnapLines.push({ position: snappedRightEdge, type: 'vertical' })
  }

  return {
    x: node.x,
    y: node.y,
    width: snappedRightEdge !== mouseX ? snappedRightEdge - node.x : mouseX - node.x,
    height: node.height,
    snapLines: activeSnapLines,
  }
}

function resizeLeftBorder(
  node: LayoutNode,
  mouseX: number,
  verticalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    if (mouseX > centerX) return 'right'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []
    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const halfW = centerX - effectiveX

    return {
      x: Math.round(centerX - halfW),
      y: node.y,
      width: Math.round(halfW * 2),
      height: node.height,
      snapLines: activeSnapLines,
    }
  }

  if (mouseX > node.x + node.width) {
    return 'right'
  }

  const snappedLeftEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const activeSnapLines: SnapLine[] = []

  if (snappedLeftEdge !== mouseX) {
    activeSnapLines.push({ position: snappedLeftEdge, type: 'vertical' })
  }

  return {
    x: snappedLeftEdge !== mouseX ? snappedLeftEdge : mouseX,
    y: node.y,
    width: node.x + node.width - (snappedLeftEdge !== mouseX ? snappedLeftEdge : mouseX),
    height: node.height,
    snapLines: activeSnapLines,
  }
}

function resizeTopBorder(
  node: LayoutNode,
  mouseY: number,
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerY = initialNode.y + initialNode.height / 2
    if (mouseY > centerY) return 'bottom'

    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY
    const halfH = centerY - effectiveY

    return {
      x: node.x,
      y: Math.round(centerY - halfH),
      width: node.width,
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseY > node.y + node.height) {
    return 'bottom'
  }

  const snappedTopEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
  const activeSnapLines: SnapLine[] = []

  if (snappedTopEdge !== mouseY) {
    activeSnapLines.push({ position: snappedTopEdge, type: 'horizontal' })
  }

  return {
    x: node.x,
    y: snappedTopEdge !== mouseY ? snappedTopEdge : mouseY,
    width: node.width,
    height: node.y + node.height - (snappedTopEdge !== mouseY ? snappedTopEdge : mouseY),
    snapLines: activeSnapLines,
  }
}

function resizeBottomBorder(
  node: LayoutNode,
  mouseY: number,
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerY = initialNode.y + initialNode.height / 2
    if (mouseY < centerY) return 'top'

    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY
    const halfH = effectiveY - centerY

    return {
      x: node.x,
      y: Math.round(centerY - halfH),
      width: node.width,
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseY < node.y) {
    return 'top'
  }

  const snappedBottomEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
  const activeSnapLines: SnapLine[] = []

  if (snappedBottomEdge !== mouseY) {
    activeSnapLines.push({ position: snappedBottomEdge, type: 'horizontal' })
  }

  return {
    x: node.x,
    y: node.y,
    width: node.width,
    height: snappedBottomEdge !== mouseY ? snappedBottomEdge - node.y : mouseY - node.y,
    snapLines: activeSnapLines,
  }
}

const resizeCorner = (
  e: MouseEvent,
  canvas: HTMLCanvasElement,
  node: LayoutNode,
  nodes: LayoutNode[],
  cornerClicked: Extract<MouseAction, { type: 'resize' }>['corner'],
  view: CanvasView,
  initialNode: { x: number; y: number; width: number; height: number },
) => {
  const mousePosition = getMousePosition(e, canvas, view)
  const altKey = e.altKey

  const { verticalLines, horizontalLines } = getVerticalAndHorizontalLines(
    node,
    nodes,
    view.virtualWidth,
    view.virtualHeight,
  )

  switch (cornerClicked) {
    case 'bottom-right': {
      return resizeBottomRightCorner(
        node,
        mousePosition.x,
        mousePosition.y,
        verticalLines,
        horizontalLines,
        altKey,
        initialNode,
      )
    }
    case 'bottom-left': {
      return resizeBottomLeftCorner(
        node,
        mousePosition.x,
        mousePosition.y,
        verticalLines,
        horizontalLines,
        altKey,
        initialNode,
      )
    }
    case 'top-right': {
      return resizeTopRightCorner(
        node,
        mousePosition.x,
        mousePosition.y,
        verticalLines,
        horizontalLines,
        altKey,
        initialNode,
      )
    }
    case 'top-left': {
      return resizeTopLeftCorner(
        node,
        mousePosition.x,
        mousePosition.y,
        verticalLines,
        horizontalLines,
        altKey,
        initialNode,
      )
    }

    default:
      throw new Error('Unknown corner clicked')
  }
}

function resizeBottomRightCorner(
  node: LayoutNode,
  mouseX: number,
  mouseY: number,
  verticalLines: number[],
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    const centerY = initialNode.y + initialNode.height / 2

    if (mouseX < centerX) return 'bottom-left'
    if (mouseY < centerY) return 'top-right'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []

    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY

    const halfW = effectiveX - centerX
    const halfH = effectiveY - centerY

    return {
      x: Math.round(centerX - halfW),
      y: Math.round(centerY - halfH),
      width: Math.round(halfW * 2),
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseX < node.x) {
    return 'bottom-left'
  }

  if (mouseY < node.y) {
    return 'top-right'
  }

  // Calculate potential new dimensions
  const newWidth = mouseX - node.x
  const newHeight = mouseY - node.y

  const snappedRightEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const snappedBottomEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)

  const activeSnapLines: SnapLine[] = []

  // Determine final dimensions with snap
  let finalWidth = newWidth
  let finalHeight = newHeight

  // Only check edge snaps
  if (snappedRightEdge !== mouseX) {
    finalWidth = snappedRightEdge - node.x
    activeSnapLines.push({ position: snappedRightEdge, type: 'vertical' })
  }

  if (snappedBottomEdge !== mouseY) {
    finalHeight = snappedBottomEdge - node.y
    activeSnapLines.push({ position: snappedBottomEdge, type: 'horizontal' })
  }
  return {
    x: node.x,
    y: node.y,
    width: finalWidth,
    height: finalHeight,
    snapLines: activeSnapLines,
  }
}
function resizeTopRightCorner(
  node: LayoutNode,
  mouseX: number,
  mouseY: number,
  verticalLines: number[],
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    const centerY = initialNode.y + initialNode.height / 2

    if (mouseX < centerX) return 'top-left'
    if (mouseY > centerY) return 'bottom-right'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []

    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY

    const halfW = effectiveX - centerX
    const halfH = centerY - effectiveY

    return {
      x: Math.round(centerX - halfW),
      y: Math.round(centerY - halfH),
      width: Math.round(halfW * 2),
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseX < node.x) {
    return 'top-left'
  }

  if (mouseY > node.y + node.height) {
    return 'bottom-right'
  }

  const newWidth = mouseX - node.x
  const newHeight = node.y + node.height - mouseY

  const snappedRightEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const snappedTopEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)

  const activeSnapLines: SnapLine[] = []
  let finalWidth = newWidth
  let finalHeight = newHeight

  if (snappedRightEdge !== mouseX) {
    finalWidth = snappedRightEdge - node.x
    activeSnapLines.push({ position: snappedRightEdge, type: 'vertical' })
  }

  if (snappedTopEdge !== mouseY) {
    finalHeight = node.y + node.height - snappedTopEdge
    activeSnapLines.push({ position: snappedTopEdge, type: 'horizontal' })
  }

  return {
    x: node.x,
    y: snappedTopEdge !== mouseY ? snappedTopEdge : mouseY,
    width: finalWidth,
    height: finalHeight,
    snapLines: activeSnapLines,
  }
}

function resizeBottomLeftCorner(
  node: LayoutNode,
  mouseX: number,
  mouseY: number,
  verticalLines: number[],
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    const centerY = initialNode.y + initialNode.height / 2

    if (mouseX > centerX) return 'bottom-right'
    if (mouseY < centerY) return 'top-left'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []

    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY

    const halfW = centerX - effectiveX
    const halfH = effectiveY - centerY

    return {
      x: Math.round(centerX - halfW),
      y: Math.round(centerY - halfH),
      width: Math.round(halfW * 2),
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseX > node.x + node.width) {
    return 'bottom-right'
  }

  if (mouseY < node.y) {
    return 'top-left'
  }

  const newWidth = node.x + node.width - mouseX
  const newHeight = mouseY - node.y

  const snappedLeftEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const snappedBottomEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)

  const activeSnapLines: SnapLine[] = []
  let finalWidth = newWidth
  let finalHeight = newHeight

  if (snappedLeftEdge !== mouseX) {
    finalWidth = node.x + node.width - snappedLeftEdge
    activeSnapLines.push({ position: snappedLeftEdge, type: 'vertical' })
  }

  if (snappedBottomEdge !== mouseY) {
    finalHeight = snappedBottomEdge - node.y
    activeSnapLines.push({ position: snappedBottomEdge, type: 'horizontal' })
  }

  return {
    x: snappedLeftEdge !== mouseX ? snappedLeftEdge : mouseX,
    y: node.y,
    width: finalWidth,
    height: finalHeight,
    snapLines: activeSnapLines,
  }
}

function resizeTopLeftCorner(
  node: LayoutNode,
  mouseX: number,
  mouseY: number,
  verticalLines: number[],
  horizontalLines: number[],
  altKey: boolean,
  initialNode: { x: number; y: number; width: number; height: number },
) {
  if (altKey) {
    const centerX = initialNode.x + initialNode.width / 2
    const centerY = initialNode.y + initialNode.height / 2

    if (mouseX > centerX) return 'top-right'
    if (mouseY > centerY) return 'bottom-left'

    const snappedX = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
    const snappedY = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)
    const activeSnapLines: SnapLine[] = []

    const effectiveX =
      snappedX !== mouseX
        ? (activeSnapLines.push({ position: snappedX, type: 'vertical' }), snappedX)
        : mouseX
    const effectiveY =
      snappedY !== mouseY
        ? (activeSnapLines.push({ position: snappedY, type: 'horizontal' }), snappedY)
        : mouseY

    const halfW = centerX - effectiveX
    const halfH = centerY - effectiveY

    return {
      x: Math.round(centerX - halfW),
      y: Math.round(centerY - halfH),
      width: Math.round(halfW * 2),
      height: Math.round(halfH * 2),
      snapLines: activeSnapLines,
    }
  }

  if (mouseX > node.x + node.width) {
    return 'top-right'
  }

  if (mouseY > node.y + node.height) {
    return 'bottom-left'
  }

  const newWidth = node.x + node.width - mouseX
  const newHeight = node.y + node.height - mouseY

  const snappedLeftEdge = findNearestSnap(mouseX, verticalLines, SNAP_THRESHOLD)
  const snappedTopEdge = findNearestSnap(mouseY, horizontalLines, SNAP_THRESHOLD)

  const activeSnapLines: SnapLine[] = []
  let finalWidth = newWidth
  let finalHeight = newHeight

  if (snappedLeftEdge !== mouseX) {
    finalWidth = node.x + node.width - snappedLeftEdge
    activeSnapLines.push({ position: snappedLeftEdge, type: 'vertical' })
  }

  if (snappedTopEdge !== mouseY) {
    finalHeight = node.y + node.height - snappedTopEdge
    activeSnapLines.push({ position: snappedTopEdge, type: 'horizontal' })
  }

  return {
    x: snappedLeftEdge !== mouseX ? snappedLeftEdge : mouseX,
    y: snappedTopEdge !== mouseY ? snappedTopEdge : mouseY,
    width: finalWidth,
    height: finalHeight,
    snapLines: activeSnapLines,
  }
}

const getSnapPositions = (
  currentNode: LayoutNode,
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
): SnapLine[] =>
  collectAndSortSnapLines(
    { x: currentNode.x + currentNode.width / 2, y: currentNode.y + currentNode.height / 2 },
    (node) => node === currentNode,
    nodes,
    canvasWidth,
    canvasHeight,
  )

const findNearestSnap = (position: number, snapPositions: number[], threshold: number) => {
  for (const snapPos of snapPositions) {
    if (Math.abs(position - snapPos) <= threshold) {
      return snapPos
    }
  }
  return position
}

const applySnapping = (
  newX: number,
  newY: number,
  node: LayoutNode,
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
  disableSnap: boolean = false,
) => {
  if (disableSnap) {
    return { x: newX, y: newY, snapLines: [] as SnapLine[] }
  }
  const { verticalLines, horizontalLines, verticalSnapLines, horizontalSnapLines } =
    getVerticalAndHorizontalLines(node, nodes, canvasWidth, canvasHeight)

  const activeSnapLines: SnapLine[] = []
  const pushSnap = (position: number, type: 'vertical' | 'horizontal') => {
    const source = (type === 'vertical' ? verticalSnapLines : horizontalSnapLines).find(
      (line) => line.position === position,
    )?.sourceNodeIndex
    activeSnapLines.push({ position, type, sourceNodeIndex: source })
  }

  let finalX = newX
  let finalY = newY

  // calculate rotated corners
  if (node.rotation) {
    const corners = [
      { x: newX, y: newY },
      { x: newX + node.width, y: newY },
      { x: newX, y: newY + node.height },
      { x: newX + node.width, y: newY + node.height },
    ]

    const center = {
      x: newX + node.width / 2,
      y: newY + node.height / 2,
    }

    // rotated positions
    const rotatedCorners = corners.map((corner) => rotate(corner, center, node.rotation))

    // rotated corner against snap lines
    for (const corner of rotatedCorners) {
      const snappedX = findNearestSnap(corner.x, verticalLines, SNAP_THRESHOLD)
      const snappedY = findNearestSnap(corner.y, horizontalLines, SNAP_THRESHOLD)

      if (snappedX !== corner.x || snappedY !== corner.y) {
        if (snappedX !== corner.x) pushSnap(snappedX, 'vertical')
        if (snappedY !== corner.y) pushSnap(snappedY, 'horizontal')

        const adjustment = {
          x: snappedX - corner.x,
          y: snappedY - corner.y,
        }

        return {
          x: newX + adjustment.x,
          y: newY + adjustment.y,
          snapLines: activeSnapLines,
        }
      }
    }
  } else {
    // Handle center snapping
    const centerX = newX + node.width / 2
    const centerY = newY + node.height / 2
    const snappedCenterX = findNearestSnap(centerX, verticalLines, SNAP_THRESHOLD)
    const snappedCenterY = findNearestSnap(centerY, horizontalLines, SNAP_THRESHOLD)

    // Handle edge snapping
    const leftEdge = findNearestSnap(newX, verticalLines, SNAP_THRESHOLD)
    const rightEdge = findNearestSnap(newX + node.width, verticalLines, SNAP_THRESHOLD)
    const topEdge = findNearestSnap(newY, horizontalLines, SNAP_THRESHOLD)
    const bottomEdge = findNearestSnap(newY + node.height, horizontalLines, SNAP_THRESHOLD)

    // Vertical snapping (X axis): center wins if it has a target; otherwise
    // the edge whose snap distance is tighter takes it. Right used to always
    // beat left from a pure order-in-code bias — picking the closer one feels
    // more like "the edge the user was actually aiming at."
    if (snappedCenterX !== centerX) {
      finalX = snappedCenterX - node.width / 2
      pushSnap(snappedCenterX, 'vertical')
    } else {
      const rightOffset =
        rightEdge !== newX + node.width ? Math.abs(rightEdge - (newX + node.width)) : Infinity
      const leftOffset = leftEdge !== newX ? Math.abs(leftEdge - newX) : Infinity
      if (rightOffset <= leftOffset && rightOffset !== Infinity) {
        finalX = rightEdge - node.width
        pushSnap(rightEdge, 'vertical')
      } else if (leftOffset !== Infinity) {
        finalX = leftEdge
        pushSnap(leftEdge, 'vertical')
      }
    }

    // Horizontal snapping (Y axis): same rule — center first, then tighter edge.
    if (snappedCenterY !== centerY) {
      finalY = snappedCenterY - node.height / 2
      pushSnap(snappedCenterY, 'horizontal')
    } else {
      const bottomOffset =
        bottomEdge !== newY + node.height ? Math.abs(bottomEdge - (newY + node.height)) : Infinity
      const topOffset = topEdge !== newY ? Math.abs(topEdge - newY) : Infinity
      if (bottomOffset <= topOffset && bottomOffset !== Infinity) {
        finalY = bottomEdge - node.height
        pushSnap(bottomEdge, 'horizontal')
      } else if (topOffset !== Infinity) {
        finalY = topEdge
        pushSnap(topEdge, 'horizontal')
      }
    }
  }

  return {
    x: finalX,
    y: finalY,
    snapLines: activeSnapLines,
  }
}

const getVerticalAndHorizontalLines = (
  node: LayoutNode,
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
) => {
  const snapLines = getSnapPositions(node, nodes, canvasWidth, canvasHeight)
  const verticalSnapLines = snapLines.filter((line) => line.type === 'vertical')
  const horizontalSnapLines = snapLines.filter((line) => line.type === 'horizontal')
  return {
    verticalLines: verticalSnapLines.map((line) => line.position),
    horizontalLines: horizontalSnapLines.map((line) => line.position),
    // Full SnapLine[] kept alongside so the drag path can look up which node
    // produced a given snap position (for the target-highlight rect). Resize
    // callers ignore these extra fields.
    verticalSnapLines,
    horizontalSnapLines,
  }
}

// Get snap positions for multi-selection (excludes all selected nodes from snap targets)
const getSnapPositionsForMultiSelect = (
  bounds: { x: number; y: number; width: number; height: number },
  selectedIndices: number[],
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
): SnapLine[] =>
  collectAndSortSnapLines(
    { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
    (_node, index) => selectedIndices.includes(index),
    nodes,
    canvasWidth,
    canvasHeight,
  )

/** Build candidate snap lines and return them sorted by spatial distance from
 *  the dragged object to each candidate's source node. Sorting matters because
 *  findNearestSnap returns the first candidate within threshold — so on a
 *  crowded canvas, the closest neighbor wins instead of whichever node
 *  happened to be earlier in `nodes`. Canvas borders are treated as infinitely
 *  far so they only win when no node-edge candidate is in range. */
const collectAndSortSnapLines = (
  origin: { x: number; y: number },
  exclude: (node: LayoutNode, index: number) => boolean,
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
): SnapLine[] => {
  const entries: { line: SnapLine; distance: number }[] = []

  const pushCanvasBorder = (line: SnapLine) => entries.push({ line, distance: Infinity })
  pushCanvasBorder({ position: 0, type: 'horizontal' })
  pushCanvasBorder({ position: 0, type: 'vertical' })
  pushCanvasBorder({ position: canvasWidth, type: 'vertical' })
  pushCanvasBorder({ position: canvasHeight, type: 'horizontal' })
  pushCanvasBorder({ position: canvasWidth / 2, type: 'vertical' })
  pushCanvasBorder({ position: canvasHeight / 2, type: 'horizontal' })

  nodes.forEach((node, index) => {
    if (exclude(node, index)) return
    const centerX = node.x + node.width / 2
    const centerY = node.y + node.height / 2
    const distance = Math.hypot(centerX - origin.x, centerY - origin.y)
    const push = (line: Omit<SnapLine, 'sourceNodeIndex'>) =>
      entries.push({ line: { ...line, sourceNodeIndex: index }, distance })

    if (node.rotation) {
      const corners = [
        { x: node.x, y: node.y },
        { x: node.x + node.width, y: node.y },
        { x: node.x, y: node.y + node.height },
        { x: node.x + node.width, y: node.y + node.height },
      ]
      const rotatedCorners = corners.map((corner) =>
        rotate(corner, { x: centerX, y: centerY }, node.rotation),
      )
      rotatedCorners.forEach((corner) => {
        push({ position: corner.x, type: 'vertical' })
        push({ position: corner.y, type: 'horizontal' })
      })
    } else {
      push({ position: node.x, type: 'vertical' })
      push({ position: centerX, type: 'vertical' })
      push({ position: node.x + node.width, type: 'vertical' })
      push({ position: node.y, type: 'horizontal' })
      push({ position: centerY, type: 'horizontal' })
      push({ position: node.y + node.height, type: 'horizontal' })
    }
  })

  entries.sort((a, b) => a.distance - b.distance)
  return entries.map((entry) => entry.line)
}

// Apply snapping for multi-selection based on bounding box
const applyMultiSnapping = (
  bounds: { x: number; y: number; width: number; height: number },
  selectedIndices: number[],
  nodes: LayoutNode[],
  canvasWidth: number,
  canvasHeight: number,
  disableSnap: boolean = false,
): { deltaX: number; deltaY: number; snapLines: SnapLine[] } => {
  if (disableSnap) {
    return { deltaX: 0, deltaY: 0, snapLines: [] }
  }
  const snapLines = getSnapPositionsForMultiSelect(
    bounds,
    selectedIndices,
    nodes,
    canvasWidth,
    canvasHeight,
  )
  const verticalSnapLines = snapLines.filter((l) => l.type === 'vertical')
  const horizontalSnapLines = snapLines.filter((l) => l.type === 'horizontal')
  const verticalLines = verticalSnapLines.map((l) => l.position)
  const horizontalLines = horizontalSnapLines.map((l) => l.position)

  const activeSnapLines: SnapLine[] = []
  const pushSnap = (position: number, type: 'vertical' | 'horizontal') => {
    const source = (type === 'vertical' ? verticalSnapLines : horizontalSnapLines).find(
      (line) => line.position === position,
    )?.sourceNodeIndex
    activeSnapLines.push({ position, type, sourceNodeIndex: source })
  }
  let deltaX = 0
  let deltaY = 0

  // Check center snapping
  const centerX = bounds.x + bounds.width / 2
  const centerY = bounds.y + bounds.height / 2
  const snappedCenterX = findNearestSnap(centerX, verticalLines, SNAP_THRESHOLD)
  const snappedCenterY = findNearestSnap(centerY, horizontalLines, SNAP_THRESHOLD)

  // Check edge snapping
  const leftEdge = findNearestSnap(bounds.x, verticalLines, SNAP_THRESHOLD)
  const rightEdge = findNearestSnap(bounds.x + bounds.width, verticalLines, SNAP_THRESHOLD)
  const topEdge = findNearestSnap(bounds.y, horizontalLines, SNAP_THRESHOLD)
  const bottomEdge = findNearestSnap(bounds.y + bounds.height, horizontalLines, SNAP_THRESHOLD)

  // Vertical snapping (X axis): center first, then the tighter of left/right.
  if (snappedCenterX !== centerX) {
    deltaX = snappedCenterX - centerX
    pushSnap(snappedCenterX, 'vertical')
  } else {
    const rightOffset =
      rightEdge !== bounds.x + bounds.width
        ? Math.abs(rightEdge - (bounds.x + bounds.width))
        : Infinity
    const leftOffset = leftEdge !== bounds.x ? Math.abs(leftEdge - bounds.x) : Infinity
    if (rightOffset <= leftOffset && rightOffset !== Infinity) {
      deltaX = rightEdge - (bounds.x + bounds.width)
      pushSnap(rightEdge, 'vertical')
    } else if (leftOffset !== Infinity) {
      deltaX = leftEdge - bounds.x
      pushSnap(leftEdge, 'vertical')
    }
  }

  // Horizontal snapping (Y axis): same rule.
  if (snappedCenterY !== centerY) {
    deltaY = snappedCenterY - centerY
    pushSnap(snappedCenterY, 'horizontal')
  } else {
    const bottomOffset =
      bottomEdge !== bounds.y + bounds.height
        ? Math.abs(bottomEdge - (bounds.y + bounds.height))
        : Infinity
    const topOffset = topEdge !== bounds.y ? Math.abs(topEdge - bounds.y) : Infinity
    if (bottomOffset <= topOffset && bottomOffset !== Infinity) {
      deltaY = bottomEdge - (bounds.y + bounds.height)
      pushSnap(bottomEdge, 'horizontal')
    } else if (topOffset !== Infinity) {
      deltaY = topEdge - bounds.y
      pushSnap(topEdge, 'horizontal')
    }
  }

  return { deltaX, deltaY, snapLines: activeSnapLines }
}

export const getCursorStyle = (
  mouseAction: MouseAction,
  selectedNodeControls?: {
    isRotationHandle?: boolean
    cornerClicked?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
    borderClicked?: 'top' | 'bottom' | 'left' | 'right'
  },
) => {
  if (mouseAction.type !== 'none') {
    if (mouseAction.type === 'resize') {
      if (mouseAction.corner) {
        return mouseAction.corner === 'top-left' || mouseAction.corner === 'bottom-right'
          ? 'nwse-resize'
          : 'nesw-resize'
      }
      if (mouseAction.border) {
        return mouseAction.border === 'top' || mouseAction.border === 'bottom'
          ? 'ns-resize'
          : 'ew-resize'
      }
    }
    if (mouseAction.type === 'rotate') return 'move'
  }

  if (selectedNodeControls) {
    if (selectedNodeControls.isRotationHandle) return 'move'
    if (selectedNodeControls.cornerClicked) {
      return selectedNodeControls.cornerClicked === 'top-left' ||
        selectedNodeControls.cornerClicked === 'bottom-right'
        ? 'nwse-resize'
        : 'nesw-resize'
    }
    if (selectedNodeControls.borderClicked) {
      return selectedNodeControls.borderClicked === 'top' ||
        selectedNodeControls.borderClicked === 'bottom'
        ? 'ns-resize'
        : 'ew-resize'
    }
  }

  return 'default'
}
